import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import { apiFetch, API_BASE } from '../apiFetch';
import PersonaIndoorSelector from '../components/locutor/PersonaIndoorSelector';
import CadastralClarificationReview from '../components/CadastralClarificationReview';

const ArrowIcon = ({ direction = 'right', size = 12, color = '#94a3b8' }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d={direction === 'right' ? 'M2 6h8M7 3l3 3-3 3' : 'M10 6H2M5 3L2 6l3 3'}
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ============================================
// CONSTANTES
// ============================================

const TOTAL_STEPS = 6;

const SEMANTIC_FIELD_DEFAULT_ENABLED = true;
const BROADCAST_DISCLOSURE_DEFAULT_ENABLED = false;

function consentMapValue(map, field, defaultValue) {
  if (!map || typeof map !== 'object') return defaultValue;
  if (!Object.prototype.hasOwnProperty.call(map, field)) return defaultValue;
  return map[field] === true;
}

function semanticFieldEnabled(usuario, field) {
  return consentMapValue(usuario?.semanticFieldConsent, field, SEMANTIC_FIELD_DEFAULT_ENABLED);
}

function broadcastDisclosureEnabled(usuario, field) {
  return consentMapValue(usuario?.broadcastDisclosureConsent, field, BROADCAST_DISCLOSURE_DEFAULT_ENABLED);
}

// ============================================
// COMPONENTES AUXILIARES
// ============================================

const StepIndicator = ({ current, total, onStepClick }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
    {Array.from({ length: total }, (_, i) => {
      const step = i + 1;
      const isActive = step === current;
      const isDone = step < current;
      return (
        <React.Fragment key={step}>
          <div
            onClick={() => isDone && onStepClick && onStepClick(step)}
            title={isDone ? `Ir para step ${step}` : undefined}
            style={{
              width: 32, height: 32, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 600,
              background: isActive
                ? 'var(--action)'
                : isDone
                  ? 'rgba(var(--brand-rgb),0.3)'
                  : 'rgba(var(--neutral-300-rgb),0.5)',
              color: isActive || isDone ? 'var(--action-text)' : 'var(--text-muted)',
              border: isActive ? '2px solid var(--brand-400)' : '2px solid transparent',
              transition: 'all 0.3s ease',
              cursor: isDone ? 'pointer' : 'default',
            }}>
            {isDone ? '✓' : step}
          </div>
          {step < total && (
            <div style={{
              flex: 1, height: 2,
              background: isDone ? 'rgba(var(--brand-rgb),0.4)' : 'rgba(var(--neutral-300-rgb),0.4)',
              transition: 'background 0.3s ease'
            }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const ConsentToggle = ({ label, checked, onChange, title }) => (
  <span
    title={title}
    onClick={e => e.stopPropagation()}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      fontSize: 11,
      fontWeight: 500,
      color: checked ? 'var(--brand-400)' : 'var(--text-muted)',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      userSelect: 'none',
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={e => onChange(e.target.checked)}
      style={{ width: 13, height: 13, margin: 0, cursor: 'pointer' }}
    />
    <span onClick={() => onChange(!checked)}>{label}</span>
  </span>
);

const Campo = ({ label, placeholder, value, onChange, tipo = 'text', obrigatorio = false, dica = '', rightSlot = null, maxLength }) => (
  <div style={{ marginBottom: 14 }}>
    {label && (
      <label style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, fontSize: 13, fontWeight: 600,
        color: 'var(--text-primary)', marginBottom: 5
      }}>
        <span>{label} {obrigatorio && <span style={{ color: 'var(--error)' }}>*</span>}</span>
        {rightSlot}
      </label>
    )}
    <input
      type={tipo}
      className="input"
      placeholder={placeholder}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      maxLength={maxLength}
      style={{ marginBottom: 0 }}
    />
    {dica && (
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{dica}</div>
    )}
  </div>
);

const CampoTexto = ({ label, placeholder, value, onChange, dica = '', rightSlot = null }) => (
  <div style={{ marginBottom: 14 }}>
    {label && (
      <label style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, fontSize: 13, fontWeight: 600,
        color: 'var(--text-primary)', marginBottom: 5
      }}>
        <span>{label}</span>
        {rightSlot}
      </label>
    )}
    <textarea
      className="textarea"
      placeholder={placeholder}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      rows={3}
    />
    {dica && (
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{dica}</div>
    )}
  </div>
);

const Toggle = ({ label, value, onChange, dica = '', rightSlot = null }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 0', borderBottom: '1px solid rgba(var(--neutral-300-rgb),0.3)'
  }}>
    <div>
      <div style={{ fontSize: 14, color: 'var(--text-primary)' }}>{label}</div>
      {dica && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{dica}</div>}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {rightSlot}
      <div
        onClick={() => onChange(!value)}
        style={{
          width: 44, height: 24, borderRadius: 12, cursor: 'pointer',
          background: value ? 'var(--brand-500)' : 'rgba(var(--neutral-300-rgb),0.6)',
          position: 'relative', transition: 'background 0.2s ease'
        }}
      >
        <div style={{
          width: 18, height: 18, borderRadius: '50%', background: 'white',
          position: 'absolute', top: 3,
          left: value ? 23 : 3,
          transition: 'left 0.2s ease',
          boxShadow: 'none'
        }} />
      </div>
    </div>
  </div>
);

// ============================================
// FUNÇÕES DE NATURALIZAÇÃO PARA FALA
// ============================================

/**
 * Converte horário "07:00" → "7 da manhã", "20:00" → "8 da noite"
 */
function naturalizarHorario(horario) {
  if (!horario) return '';
  const [h, m] = horario.split(':').map(Number);
  if (isNaN(h)) return horario;

  // Converter 24h → 12h com período
  let hora = h;
  let periodo = '';

  if (h >= 0 && h < 6) { periodo = 'da madrugada'; }
  else if (h >= 6 && h < 12) { periodo = 'da manhã'; }
  else if (h === 12) { periodo = 'do meio-dia'; hora = 12; }
  else if (h > 12 && h < 18) { periodo = 'da tarde'; hora = h - 12; }
  else if (h >= 18 && h < 24) { periodo = 'da noite'; hora = h - 12; }

  if (h === 0) hora = 12; // meia-noite

  if (m && m > 0) {
    return `${hora} e ${m} ${periodo}`;
  }
  return `${hora} ${periodo}`;
}

/**
 * Converte telefone para leitura por extenso no padrão brasileiro
 * "66984198753" → "meia meia, nove oito quatro, dezenove, oitenta e sete, cinquenta e três"
 */
function naturalizarTelefone(tel) {
  if (!tel) return '';
  const nums = tel.replace(/\D/g, '');
  
  const digitos = {
    '0': 'zero', '1': 'um', '2': 'dois', '3': 'três',
    '4': 'quatro', '5': 'cinco', '6': 'meia', '7': 'sete',
    '8': 'oito', '9': 'nove'
  };
  
  const pares = {
    '10': 'dez', '11': 'onze', '12': 'doze', '13': 'treze',
    '14': 'quatorze', '15': 'quinze', '16': 'dezesseis',
    '17': 'dezessete', '18': 'dezoito', '19': 'dezenove',
    '20': 'vinte', '21': 'vinte e um', '22': 'vinte e dois',
    '23': 'vinte e três', '24': 'vinte e quatro', '25': 'vinte e cinco',
    '26': 'vinte e meia', '27': 'vinte e sete', '28': 'vinte e oito',
    '29': 'vinte e nove', '30': 'trinta', '31': 'trinta e um',
    '32': 'trinta e dois', '33': 'trinta e três', '34': 'trinta e quatro',
    '35': 'trinta e cinco', '36': 'trinta e meia', '37': 'trinta e sete',
    '38': 'trinta e oito', '39': 'trinta e nove',
    '40': 'quarenta', '41': 'quarenta e um', '42': 'quarenta e dois',
    '43': 'quarenta e três', '44': 'quarenta e quatro', '45': 'quarenta e cinco',
    '46': 'quarenta e meia', '47': 'quarenta e sete', '48': 'quarenta e oito',
    '49': 'quarenta e nove', '50': 'cinquenta', '51': 'cinquenta e um',
    '52': 'cinquenta e dois', '53': 'cinquenta e três', '54': 'cinquenta e quatro',
    '55': 'cinquenta e cinco', '56': 'cinquenta e meia', '57': 'cinquenta e sete',
    '58': 'cinquenta e oito', '59': 'cinquenta e nove',
    '60': 'meia zero', '61': 'meia um', '62': 'meia dois',
    '63': 'meia três', '64': 'meia quatro', '65': 'meia cinco',
    '66': 'meia meia', '67': 'meia sete', '68': 'meia oito',
    '69': 'meia nove', '70': 'setenta', '71': 'setenta e um',
    '72': 'setenta e dois', '73': 'setenta e três', '74': 'setenta e quatro',
    '75': 'setenta e cinco', '76': 'setenta e meia', '77': 'setenta e sete',
    '78': 'setenta e oito', '79': 'setenta e nove',
    '80': 'oitenta', '81': 'oitenta e um', '82': 'oitenta e dois',
    '83': 'oitenta e três', '84': 'oitenta e quatro', '85': 'oitenta e cinco',
    '86': 'oitenta e meia', '87': 'oitenta e sete', '88': 'oitenta e oito',
    '89': 'oitenta e nove', '90': 'noventa', '91': 'noventa e um',
    '92': 'noventa e dois', '93': 'noventa e três', '94': 'noventa e quatro',
    '95': 'noventa e cinco', '96': 'noventa e meia', '97': 'noventa e sete',
    '98': 'noventa e oito', '99': 'noventa e nove', '00': 'zero zero'
  };
  
  const lerPar = (par) => pares[par] || `${digitos[par[0]]} ${digitos[par[1]]}`;
  const lerDigito = (d) => digitos[d] || d;

  if (nums.length === 11) {
    const ddd = nums.slice(0, 2);
    const d9 = nums.slice(2, 3);
    const d4 = nums.slice(3, 4);
    const d5 = nums.slice(4, 5);
    const p1 = nums.slice(5, 7);
    const p2 = nums.slice(7, 9);
    const p3 = nums.slice(9, 11);
    return `${lerPar(ddd)}, ${lerDigito(d9)} ${lerDigito(d4)} ${lerDigito(d5)}, ${lerPar(p1)}, ${lerPar(p2)}, ${lerPar(p3)}`;
  } else if (nums.length === 10) {
    const ddd = nums.slice(0, 2);
    const p1 = nums.slice(2, 4);
    const p2 = nums.slice(4, 6);
    const p3 = nums.slice(6, 8);
    const p4 = nums.slice(8, 10);
    return `${lerPar(ddd)}, ${lerPar(p1)}, ${lerPar(p2)}, ${lerPar(p3)}, ${lerPar(p4)}`;
  }
  return tel;
}

/**
 * Capitaliza primeira letra de cada frase
 */
function capitalizarFrase(texto) {
  if (!texto) return '';
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

const PreviewLocutor = ({ usuario }) => {
  const nomeComercio =
    (semanticFieldEnabled(usuario, 'nomeCurto') && usuario.nomeCurto)
    || (semanticFieldEnabled(usuario, 'nome') && usuario.nome)
    || 'Sua loja';
  const cliente = semanticFieldEnabled(usuario, 'cliente')
    ? (usuario.cliente || 'cliente amigo')
    : 'cliente';
  const cidade = semanticFieldEnabled(usuario, 'cidade') ? (usuario.cidade || '') : '';
  const slogan = semanticFieldEnabled(usuario, 'slogan') && usuario.sloganMode !== 'audio'
    ? (usuario.slogan || '')
    : '';
  const whatsapp = broadcastDisclosureEnabled(usuario, 'whatsapp') ? (usuario.whatsapp || '') : '';

  const tipoAmbientePreview = semanticFieldEnabled(usuario, 'tipoAmbiente') ? usuario.tipoAmbiente : null;
  const prefixo = usuario.saudacaoPersonalizada || (tipoAmbientePreview === 'radio'
    ? 'Você está na rádio'
    : ['clinica', 'academia', 'igreja'].includes(tipoAmbientePreview) ? 'Bem-vindo à' : 'Bem-vindo ao');

  const horaAtual = new Date().getHours();
  const saudacao = horaAtual < 9
    ? (usuario.saudacaoManha || 'Bom dia')
    : horaAtual < 18
      ? (usuario.saudacaoTarde || 'Boa tarde')
      : (usuario.saudacaoNoite || 'Boa noite');

  // ── Montar frases separadas (mais natural) ──
  const frases = [];

  // Frase 1: Saudação + nome
  let abertura = `${saudacao}, ${cliente}! ${prefixo} ${nomeComercio}`;
  if (cidade) abertura += `, aqui em ${cidade}`;
  frases.push(abertura + '.');

  // Frase 2: Slogan (capitalizado)
  if (slogan) {
    frases.push(capitalizarFrase(slogan.trim()) + (slogan.trim().endsWith('.') || slogan.trim().endsWith('!') ? '' : '.'));
  }

  // Frase 3: Horário naturalizado
  if (usuario.horarioAbre && usuario.horarioFecha) {
    const abre = naturalizarHorario(usuario.horarioAbre);
    const fecha = naturalizarHorario(usuario.horarioFecha);
    frases.push(`Aberto das ${abre} às ${fecha}.`);
  }

  // Frase 4: WhatsApp naturalizado
  if (whatsapp) {
    const telNatural = naturalizarTelefone(whatsapp);
    frases.push(`Chama no WhatsApp: ${telNatural}.`);
  }

  // Frase 5: Endereço
  if (broadcastDisclosureEnabled(usuario, 'endereco') && usuario.endereco) {
    let end = `Estamos na ${usuario.endereco}`;
    if (broadcastDisclosureEnabled(usuario, 'numero') && usuario.numero) end += `, ${usuario.numero}`;
    if (usuario.bairro) end += `, bairro ${usuario.bairro}`;
    if (broadcastDisclosureEnabled(usuario, 'referencia') && usuario.referencia) end += `, ${usuario.referencia}`;
    frases.push(end + '.');
  }

  // Frase 6: Delivery
  if (usuario.delivery && semanticFieldEnabled(usuario, 'delivery')) {
    frases.push('E a gente entrega pra você!');
  }

  // Frase 7: Instagram
  if (broadcastDisclosureEnabled(usuario, 'instagram') && usuario.instagram) {
    const insta = usuario.instagram.startsWith('@') ? usuario.instagram : `@${usuario.instagram}`;
    frases.push(`Siga no Instagram: ${insta}.`);
  }

  // Frase 8: Frases especiais
  if (usuario.fraseAbertura && horaAtual < 12) {
    frases.push(capitalizarFrase(usuario.fraseAbertura));
  }
  if (usuario.fraseFechamento && horaAtual >= 18) {
    frases.push(capitalizarFrase(usuario.fraseFechamento));
  }
  if (usuario.frasePromocional) {
    frases.push(capitalizarFrase(usuario.frasePromocional));
  }

  // Juntar com espaço
  const falaCompleta = frases.join(' ');

  // Contar variáveis usadas
  const varsUsadas = [
    nomeComercio !== 'Sua loja',
    !!cidade, !!slogan, !!whatsapp,
    !!(usuario.horarioAbre && usuario.horarioFecha),
    !!(broadcastDisclosureEnabled(usuario, 'endereco') && usuario.endereco),
    !!(usuario.delivery && semanticFieldEnabled(usuario, 'delivery')),
    !!(broadcastDisclosureEnabled(usuario, 'instagram') && usuario.instagram),
    !!usuario.fraseAbertura,
    !!usuario.fraseFechamento, !!usuario.frasePromocional
  ].filter(Boolean).length;

  return (
    <div style={{
      background: 'rgba(var(--brand-rgb),0.1)',
      border: '1px solid rgba(var(--brand-rgb),0.3)',
      borderRadius: 0, padding: 16, marginTop: 16
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 10
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-400)' }}>
          Prévia ilustrativa do locutor
        </div>
        <div style={{
          fontSize: 11, color: 'var(--text-muted)',
          background: 'rgba(var(--neutral-300-rgb),0.5)',
          padding: '2px 8px', borderRadius: 8
        }}>
          {varsUsadas} variáveis ativas
        </div>
      </div>

      <div style={{
        fontSize: 14, color: 'var(--text-primary)', fontStyle: 'italic', lineHeight: 1.7,
        borderLeft: '3px solid var(--brand-500)', paddingLeft: 12
      }}>
        "{falaCompleta}"
      </div>

      <div style={{
        fontSize: 11, color: 'var(--text-muted)', marginTop: 10,
        display: 'flex', gap: 6, flexWrap: 'wrap'
      }}>
        <span>Esta composição é ilustrativa: não prova nem antecipa uma fala factual M1.</span>
        <span>Campos vazios são ignorados; fatos do M1 exigem fonte tipada e gate próprio.</span>
      </div>
    </div>
  );
};

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

function CadastroScreen({ signupInviteToken = '', publicInviteMode = false } = {}) {
  const { usuario, setUsuario, setTela } = useAppContext();
  const [errors, setErrors] = useState({});
  const [saveNotice, setSaveNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [routineEdited, setRoutineEdited] = useState(false);
  const [clarificationRefresh, setClarificationRefresh] = useState(0);
  useEffect(() => { setRoutineEdited(false); }, [usuario.id]);
  const [step, setStep] = useState(1);
  const [locutores, setLocutores]             = useState([]);
  const [previewTocando, setPreviewTocando]   = useState(null);
  const audioPreviewRef                       = useRef(null);
  const [sloganAudio, setSloganAudio] = useState(null);
  const [sloganAudioLoading, setSloganAudioLoading] = useState(false);
  const [sloganAudioError, setSloganAudioError] = useState('');

  // Verifica se o id salvo no localStorage ainda existe no servidor
  React.useEffect(() => {
    if (!usuario.id) return;
    apiFetch(`/api/usuarios/${usuario.id}`)
      .then(r => {
        if (r.status === 404) {
          // Usuario deletado do servidor — limpa localStorage
          console.log('[Cadastro] Usuário não encontrado no servidor, limpando localStorage');
          localStorage.removeItem('radio-ai-user');
          setUsuario({
            nome: '', negocio: '', cep: '', telefone: '', email: '',
            valores: 'qualidade, confiança, bom atendimento',
            publicoAlvo: '', ritmoAmbiente: '',
            clienteDescricaoLivre: '',
            clienteRotinaCompra: '',
            clienteValoriza: '',
            radioTomDesejado: '',
            radioEvitar: '',
            quantidadeLocutores: '1',
          });
          setStep(1);
        }
      })
      .catch(() => {}); // Servidor offline — não limpa
  }, []); // Roda só na montagem

  useEffect(() => {
    if (!usuario.id) {
      setSloganAudio(null);
      return;
    }
    let cancelled = false;
    apiFetch(`/api/slogan-jingle/${usuario.id}`)
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!cancelled && response.ok) setSloganAudio(data.configured ? data.audio : null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [usuario.id, usuario.sloganAudio?.contentSha256]);

  // Busca tipoProduto configurado no .env pelo instalador
  useEffect(() => {
    fetch('/api/setup-config')
      .then(r => r.json())
      .then(d => {
        if (d.configurado && d.tipoProduto && !usuario.tipoProduto) {
          set('tipoProduto', d.tipoProduto);
          // Pula step 1 direto para dados básicos
          setStep(2);
          console.log('[Cadastro] Tipo configurado pelo instalador:', d.tipoProduto);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line
  }, []);

  // Carregar lista de locutores para o seletor do step 6
  useEffect(() => {
    fetch('/api/locutor/lista')
      .then(r => r.json())
      .then(d => setLocutores(d.locutores || []))
      .catch(() => {});
  }, []);

  // Quando muda tipoProduto, garante quantidadeLocutores = 1 para indoor
  React.useEffect(() => {
    if (usuario.tipoProduto === 'indoor') {
      set('quantidadeLocutores', '1');
    }
  }, [usuario.tipoProduto]);
  const [segmentoEditado, setSegmentoEditado] = useState(null);
  const segmentoDeclarado = segmentoEditado ?? (usuario.segmentoCustom || usuario.segmento || '');
  useEffect(() => setSegmentoEditado(null), [usuario.id]);
  const isEdicao = !!usuario.id;

  // Helper para salvar campo
  const set = (campo, valor) => {
    setUsuario(prev => ({ ...prev, [campo]: valor }));
    if (errors[campo]) {
      setErrors(prev => { const c = { ...prev }; delete c[campo]; return c; });
    }
  };

  const setConsent = (mapField, field, value) => {
    setUsuario(prev => ({
      ...prev,
      [mapField]: {
        ...(prev[mapField] || {}),
        [field]: value,
      },
    }));
  };

  const semanticConsentToggle = (field, label = 'Contexto') => (
    <ConsentToggle
      label={label}
      checked={semanticFieldEnabled(usuario, field)}
      onChange={value => setConsent('semanticFieldConsent', field, value)}
      title="Autoriza contexto semântico; texto livre não vira fato M1"
    />
  );

  const disclosureConsentToggle = (field) => (
    <ConsentToggle
      label="Divulgar"
      checked={broadcastDisclosureEnabled(usuario, field)}
      onChange={value => setConsent('broadcastDisclosureConsent', field, value)}
      title="Autoriza mencionar este dado de contato, endereço ou rede social no rádio"
    />
  );

  const readAudioDuration = file => new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const audio = new Audio();
    const cleanup = () => URL.revokeObjectURL(objectUrl);
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      const duration = Number(audio.duration);
      cleanup();
      Number.isFinite(duration) ? resolve(duration) : reject(new Error('Duração do MP3 inválida'));
    };
    audio.onerror = () => { cleanup(); reject(new Error('Não foi possível ler o MP3')); };
    audio.src = objectUrl;
  });

  const uploadSloganAudio = async file => {
    if (!file || !usuario.id) return;
    setSloganAudioLoading(true);
    setSloganAudioError('');
    try {
      if (!file.name.toLowerCase().endsWith('.mp3')) throw new Error('Selecione um arquivo MP3');
      if (file.size > 8 * 1024 * 1024) throw new Error('O jingle deve ter no máximo 8 MB');
      const duration = await readAudioDuration(file);
      if (duration < 0.15 || duration > 5) throw new Error('O jingle deve durar no máximo 5 segundos');
      const form = new FormData();
      form.append('jingle', file);
      const response = await apiFetch(`/api/slogan-jingle/${usuario.id}`, { method: 'POST', body: form });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Não foi possível enviar o jingle');
      setSloganAudio(data.audio || null);
      setUsuario(prev => ({
        ...prev,
        sloganMode: 'audio',
        sloganAudio: data.audio ? {
          originalName: data.audio.originalName,
          duration: data.audio.duration,
          contentSha256: data.audio.contentSha256,
          status: data.audio.status,
        } : null,
      }));
    } catch (error) {
      setSloganAudioError(error.message || 'Não foi possível enviar o jingle');
    } finally {
      setSloganAudioLoading(false);
    }
  };

  const removeSloganAudio = async () => {
    if (!usuario.id) return;
    setSloganAudioLoading(true);
    setSloganAudioError('');
    try {
      const response = await apiFetch(`/api/slogan-jingle/${usuario.id}`, { method: 'DELETE' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Não foi possível remover o jingle');
      setSloganAudio(null);
      setUsuario(prev => {
        const next = { ...prev, sloganMode: 'text' };
        delete next.sloganAudio;
        return next;
      });
    } catch (error) {
      setSloganAudioError(error.message || 'Não foi possível remover o jingle');
    } finally {
      setSloganAudioLoading(false);
    }
  };

  // Preview de locutor no seletor do step 6
  const handlePreviewLocutor = async (locId) => {
    if (audioPreviewRef.current) {
      audioPreviewRef.current.pause();
      audioPreviewRef.current = null;
    }
    if (previewTocando === locId) { setPreviewTocando(null); return; }
    setPreviewTocando(locId);
    try {
      const res = await apiFetch('/api/locutor/preview-voz', {
        method: 'POST',
        body: JSON.stringify({
          locutorId: locId,
        }),
      });
      const data = await res.json();
      if (data.audioUrl) {
        const url = data.audioUrl.startsWith('http') ? data.audioUrl : `${API_BASE}${data.audioUrl}`;
        const audio = new Audio(url);
        audioPreviewRef.current = audio;
        audio.onended = () => setPreviewTocando(null);
        audio.play();
      } else {
        setPreviewTocando(null);
      }
    } catch { setPreviewTocando(null); }
  };

  // Buscar CEP
  const buscarCEP = async (cep) => {
    const limpo = (cep || '').replace(/\D/g, '');
    if (limpo.length !== 8) return;

    try {
      const res = await fetch(`https://viacep.com.br/ws/${limpo}/json/`);
      const data = await res.json();
      if (!data.erro) {
        set('cidade', data.localidade);
        set('estado', data.uf);
        set('endereco', data.logradouro);
        set('bairro', data.bairro);
      }
    } catch (err) {
      console.warn('Erro ao buscar CEP:', err);
    }
  };

  // Validação por step
  const validar = () => {
    const e = {};

    if (step === 1) {
      if (!usuario.tipoProduto) e.tipoProduto = 'Selecione o tipo de instalação';
    }
    if (step === 3) {
      if (!usuario.nome || usuario.nome.length < 2) e.nome = 'Nome obrigatório';
      if (!usuario.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(usuario.email)) e.email = 'Email inválido';
    }

    if (step === 3) {
      if (!segmentoDeclarado.trim()) e.segmento = 'Descreva o segmento da loja';
      if ((usuario.sloganMode || 'text') === 'text') {
        const slogan = String(usuario.slogan || '').trim();
        if ([...slogan].length > 60 || slogan.split(/\s+/).filter(Boolean).length > 10) {
          e.slogan = 'Use no máximo 60 caracteres e 10 palavras';
        }
      } else if (!sloganAudio && !usuario.sloganAudio) {
        e.slogan = 'Envie um jingle MP3 válido antes de selecionar áudio';
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const avancar = () => {
    if (!validar()) return;
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
    } else {
      salvar();
    }
  };

  const voltar = () => {
    if (step > 1) setStep(step - 1);
  };

  // Salvar no backend
  const salvar = async () => {
    setLoading(true);
    setSaveNotice('');
    try {
      const isEdicao = !!usuario.id;
      const url      = isEdicao ? `/api/usuarios/${usuario.id}` : '/api/usuarios';
      const method   = isEdicao ? 'PUT' : 'POST';

      const payload = { ...usuario };
      if (!isEdicao && signupInviteToken) {
        payload.signupInviteToken = signupInviteToken;
      }

      const res = await apiFetch(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        const novoId = data.id || data.usuario?.id;
        
        if (!isEdicao && novoId) {
          setUsuario(prev => ({ ...prev, id: novoId }));
        }

        if (isEdicao) {
          if (data.usuario) {
            setUsuario(prev => ({ ...prev, ...data.usuario }));
          }
          setErrors({});
          setRoutineEdited(false); setClarificationRefresh(previous => previous + 1);
          setSaveNotice(data.message || 'Configurações salvas. A planta e o runtime semântico não foram alterados.');
          return;
        }

        if (!isEdicao && publicInviteMode) {
          const loginRes = await fetch('/api/auth/login', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: usuario.email, senha: usuario.senha })
          });
          const loginData = await loginRes.json().catch(() => ({}));

          if (loginRes.ok && loginData.perfil?.slug) {
            localStorage.removeItem('radio-ai-jwt');
            localStorage.setItem('radio-ai-user', JSON.stringify(loginData.perfil));
            window.location.href = `/cliente/${loginData.perfil.slug}`;
            return;
          }

          setSaveNotice('Cadastro criado. Faça login com o email e senha cadastrados.');
          return;
        }

        // Indoor + novo cadastro: baixa o exe. Provisionamento seguro ocorre pelo painel autenticado.
        if (!isEdicao && usuario.tipoProduto === 'indoor') {
          try {
            const downloadRes = await fetch('/api/download/latest');
            const downloadData = await downloadRes.json();
            if (downloadData.success) {
              const link = document.createElement('a');
              link.href = downloadData.url;
              link.download = downloadData.filename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          } catch (err) {
            console.warn('Erro ao baixar exe:', err);
          }
        }

        setTela('resultado');
      } else {
        const errData = await res.json().catch(() => ({}));
        setErrors({ submit: errData.message || 'Erro ao cadastrar' });
      }
    } catch {
      setErrors({ submit: 'Erro de conexão com o servidor' });
    } finally {
      setLoading(false);
    }
  };

  // Limpa tudo e começa do zero
  const resetCadastro = () => {
    if (!window.confirm('Isso vai apagar o cadastro atual e começar do zero. Confirma?')) return;
    localStorage.removeItem('radio-ai-user');
    setUsuario({
      nome: '', negocio: '', cep: '', telefone: '', email: '',
      valores: 'qualidade, confiança, bom atendimento',
      publicoAlvo: '', ritmoAmbiente: '',
      clienteDescricaoLivre: '',
      clienteRotinaCompra: '',
      clienteValoriza: '',
      radioTomDesejado: '',
      radioEvitar: '',
      quantidadeLocutores: '4',
    });
    setStep(1);
    setErrors({});
  };

  // ============================================
  // STEPS DE TÍTULOS
  // ============================================
  const stepTitles = [
    'Tipo de Instalação',
    'Dados Básicos',
    'Sobre o Negócio',
    'Localização e Contato',
    'Funcionamento',
    'Como o Locutor Vai Falar'
  ];

  return (
    <div className="page" style={{ paddingBottom: 0 }}>
      <h1 className="page-title" style={{ marginBottom: 16 }}>
        {isEdicao ? 'Configurações' : publicInviteMode ? 'Cadastro por convite' : 'Cadastro'}
      </h1>

      <div className="page-content">
        <div className="card" style={{ maxWidth: 620, width: '100%' }}>

          <StepIndicator current={step} total={TOTAL_STEPS} onStepClick={setStep} />

          {usuario.id && !publicInviteMode && (
            <div style={{ textAlign: 'right', marginBottom: 8 }}>
              <button
                onClick={resetCadastro}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', fontSize: 12, textDecoration: 'underline',
                }}
              >
                ↩ Novo cadastro
              </button>
            </div>
          )}

          <h2 className="step-title">{stepTitles[step - 1]}</h2>

          {/* ═══════════════════════════════════════════ */}
          {/* STEP 1 — TIPO DE PRODUTO */}
          {/* ═══════════════════════════════════════════ */}
          {step === 1 && (
            <>
              <div style={{ marginBottom: 24 }}>
                <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, margin: '0 0 20px' }}>
                  Selecione como o sistema será usado nesta instalação.
                  Isso define os locutores gerados e as funcionalidades disponíveis.
                </p>

                {[
                  {
                    id: 'indoor',
                    titulo: 'Rádio Indoor',
                    desc: 'Para comércios e estabelecimentos. 1 locutor personalizado, programação contínua, spots automáticos.',
                    detalhes: ['1 locutor gerado do cadastro', 'Memória de 7 dias', 'Spots e promoções', 'Plug and play'],
                  },
                ].map(opt => (
                  <div
                    key={opt.id}
                    onClick={isEdicao ? undefined : () => set('tipoProduto', opt.id)}
                    style={{
                      cursor: isEdicao ? 'not-allowed' : 'pointer',
                      marginBottom: 14,
                      padding: '18px 20px',
                      borderRadius: 0,
                      border: usuario.tipoProduto === opt.id
                        ? '2px solid var(--brand-500)'
                        : '1px solid rgba(var(--neutral-300-rgb),0.4)',
                      background: usuario.tipoProduto === opt.id
                        ? 'rgba(var(--brand-rgb),0.12)'
                        : 'rgba(var(--neutral-400-rgb),0.4)',
                      transition: 'all 0.15s',
                      opacity: isEdicao ? 0.6 : 1,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 600 }}>{opt.titulo}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>{opt.desc}</div>
                      </div>
                      {usuario.tipoProduto === opt.id && (
                        <div style={{
                          marginLeft: 'auto', width: 22, height: 22, borderRadius: 11,
                          background: 'var(--brand-500)', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', flexShrink: 0,
                        }}>
                          <span style={{ color: '#fff', fontSize: 13 }}>✓</span>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                      {opt.detalhes.map(d => (
                        <span key={d} style={{
                          fontSize: 11, padding: '3px 10px', borderRadius: 20,
                          background: 'rgba(var(--neutral-300-rgb),0.4)', color: 'var(--text-secondary)',
                        }}>{d}</span>
                      ))}
                    </div>
                  </div>
                ))}

                {errors.tipoProduto && (
                  <div className="alert alert-error" style={{ marginTop: 8 }}>{errors.tipoProduto}</div>
                )}
                {isEdicao && (
                  <p style={{
                    fontSize: 12,
                    color: 'var(--text-muted)',
                    textAlign: 'center',
                    marginTop: 12
                  }}>
                    Para alterar o tipo de produto entre em contato com iafm.io
                  </p>
                )}
              </div>
            </>
          )}

          {/* ═══════════════════════════════════════════ */}
          {/* STEP 3 — SOBRE O NEGÓCIO (segmento) */}
          {/* ═══════════════════════════════════════════ */}
          {step === 3 && (
            <>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Estes dados ajudam identidade, estilo e caminhos autorizados. A fala factual M1 usa apenas campos tipados previstos no contrato.
              </div>
              <CampoTexto
                label="Segmento"
                placeholder="Ex: Supermercado de bairro e produtos de conveniência"
                value={segmentoDeclarado}
                onChange={value => { setSegmentoEditado(value); set('segmentoCustom', value); }}
                dica="Descreva o negócio com suas palavras."
                rightSlot={<>
                  {semanticConsentToggle('segmentoCustom')}
                  {usuario.segmento && semanticConsentToggle('segmento', 'Contexto anterior')}
                </>}
              />
              {errors.segmento && <div className="alert alert-error" style={{ marginBottom: 10 }}>{errors.segmento}</div>}

              <div style={{ marginBottom: 14 }}>
                <label style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 10, fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 7,
                }}>
                  <span>Assinatura da loja</span>
                  {semanticConsentToggle('slogan')}
                </label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  {[
                    ['text', 'Texto falado'],
                    ['audio', 'Jingle MP3'],
                  ].map(([mode, label]) => (
                    <button
                      type="button"
                      key={mode}
                      className={(usuario.sloganMode || 'text') === mode ? 'btn' : 'btn btn-secondary'}
                      onClick={() => set('sloganMode', mode)}
                      disabled={mode === 'audio' && !usuario.id && !sloganAudio}
                      style={{ flex: 1, fontSize: 12, padding: '8px 10px' }}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {(usuario.sloganMode || 'text') === 'text' ? (
                  <Campo
                    placeholder="Ex: Sempre com você!"
                    value={usuario.slogan}
                    onChange={v => set('slogan', v)}
                    maxLength={60}
                    dica={`${[...(usuario.slogan || '')].length}/60 caracteres · ${(usuario.slogan || '').trim().split(/\s+/).filter(Boolean).length}/10 palavras. Quando usado, fecha a locução.`}
                  />
                ) : (
                  <div style={{
                    padding: 12, border: '1px solid rgba(var(--neutral-300-rgb),0.5)',
                    background: 'rgba(var(--neutral-400-rgb),0.35)',
                  }}>
                    {sloganAudio ? (
                      <div style={{ display: 'grid', gap: 8 }}>
                        <div style={{ color: 'var(--text-primary)', fontSize: 12, fontWeight: 600 }}>
                          {sloganAudio.originalName || 'Jingle MP3'} · {Number(sloganAudio.duration || 0).toFixed(2)}s
                        </div>
                        {sloganAudio.previewUrl && (
                          <audio controls preload="none" src={sloganAudio.previewUrl.startsWith('http') ? sloganAudio.previewUrl : `${API_BASE}${sloganAudio.previewUrl}`} style={{ width: '100%', height: 34 }} />
                        )}
                        <button type="button" className="btn btn-secondary" onClick={removeSloganAudio} disabled={sloganAudioLoading}>
                          Remover jingle
                        </button>
                      </div>
                    ) : (
                      <label style={{ display: 'block', cursor: usuario.id ? 'pointer' : 'not-allowed' }}>
                        <input
                          type="file"
                          accept="audio/mpeg,.mp3"
                          disabled={!usuario.id || sloganAudioLoading}
                          onChange={event => {
                            const file = event.target.files?.[0];
                            event.target.value = '';
                            if (file) uploadSloganAudio(file);
                          }}
                          style={{ width: '100%', color: 'var(--text-secondary)', fontSize: 12 }}
                        />
                      </label>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                      MP3 de 0,15 a 5,00 segundos e até 8 MB. O Radio Player normaliza para −16 LUFS antes de tocar.
                      {!usuario.id ? ' Salve o cadastro antes de enviar o arquivo.' : ''}
                    </div>
                  </div>
                )}
                {errors.slogan && <div className="alert alert-error" style={{ marginTop: 8 }}>{errors.slogan}</div>}
                {sloganAudioError && <div className="alert alert-error" style={{ marginTop: 8 }}>{sloganAudioError}</div>}
              </div>

              <CampoTexto
                label="Características da loja"
                placeholder="Ex: loja de bairro, atendimento cuidadoso e ambiente tranquilo"
                value={usuario.descricaoNegocio}
                onChange={v => set('descricaoNegocio', v)}
                dica="Contexto editorial curto. Não autoriza o M1 a inferir produtos, serviços ou afirmações."
                rightSlot={semanticConsentToggle('descricaoNegocio')}
              />

              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12 }}>
                  Preferências antigas de identidade (opcional)
                </summary>
                <div style={{ marginTop: 10 }}>
                  <Campo
                    label="Palavras que definem sua loja"
                    placeholder="Ex: qualidade, confiança, preço justo"
                    value={usuario.valores}
                    onChange={v => set('valores', v)}
                    dica="Campo legado preservado para compatibilidade; não vira afirmação factual M1."
                    rightSlot={semanticConsentToggle('valores')}
                  />
                </div>
              </details>
            </>
          )}

          {/* ═══════════════════════════════════════════ */}
          {/* STEP 2 — DADOS BÁSICOS */}
          {/* ═══════════════════════════════════════════ */}
          {step === 2 && (
            <>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Essas informações identificam sua loja. Algumas aparecerão nas falas do locutor.
              </div>
              <Campo
                label="Nome da Loja"
                placeholder="Ex: Supermercado Central"
                value={usuario.nome}
                onChange={v => set('nome', v)}
                obrigatorio
                dica="O locutor usa este nome para apresentar sua loja"
                rightSlot={semanticConsentToggle('nome')}
              />
              {errors.nome && <div className="alert alert-error" style={{ marginBottom: 10 }}>{errors.nome}</div>}

              <Campo
                label="Nome Curto"
                placeholder="Ex: Central"
                value={usuario.nomeCurto}
                onChange={v => set('nomeCurto', v)}
                dica="Versão abreviada para o locutor usar em falas rápidas"
                rightSlot={semanticConsentToggle('nomeCurto')}
              />

              <Campo
                label="Email"
                placeholder="contato@empresa.com"
                value={usuario.email}
                onChange={v => set('email', v)}
                tipo="email"
                obrigatorio
                dica="Para acesso ao sistema — não aparece nas falas"
              />
              {errors.email && <div className="alert alert-error" style={{ marginBottom: 10 }}>{errors.email}</div>}

              <Campo
                label="Telefone"
                placeholder="(66) 9 8419-8753"
                value={usuario.telefone}
                onChange={v => set('telefone', v)}
                dica="O locutor pode divulgar este número para seus clientes"
                rightSlot={disclosureConsentToggle('telefone')}
              />

              <Campo
                label="WhatsApp"
                placeholder="(66) 9 8419-8753"
                value={usuario.whatsapp}
                onChange={v => set('whatsapp', v)}
                dica="O locutor pode mencionar seu WhatsApp nas falas"
                rightSlot={disclosureConsentToggle('whatsapp')}
              />
            </>
          )}



          {/* ═══════════════════════════════════════════ */}
          {/* STEP 3 — LOCALIZAÇÃO E CONTATO */}
          {/* ═══════════════════════════════════════════ */}
          {step === 4 && (
            <>
              <Campo
                label="CEP"
                placeholder="78650-000"
                value={usuario.cep}
                onChange={v => { set('cep', v); if (v.replace(/\D/g, '').length === 8) buscarCEP(v); }}
                dica="Preenche cidade e estado automaticamente"
              />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 10 }}>
                <Campo
                  label="Cidade"
                  placeholder="Santa Terezinha"
                  value={usuario.cidade}
                  onChange={v => set('cidade', v)}
                  rightSlot={semanticConsentToggle('cidade')}
                />
                <Campo
                  label="UF"
                  placeholder="MT"
                  value={usuario.estado}
                  onChange={v => set('estado', v)}
                />
              </div>

              <Campo
                label="Bairro"
                placeholder="Centro"
                value={usuario.bairro}
                onChange={v => set('bairro', v)}
                dica="O locutor menciona sua localização nas falas"
                rightSlot={semanticConsentToggle('bairro')}
              />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 10 }}>
                <Campo
                  label="Endereço"
                  placeholder="Av. Brasil"
                  value={usuario.endereco}
                  onChange={v => set('endereco', v)}
                  rightSlot={disclosureConsentToggle('endereco')}
                />
                <Campo
                  label="Número"
                  placeholder="500"
                  value={usuario.numero}
                  onChange={v => set('numero', v)}
                  rightSlot={disclosureConsentToggle('numero')}
                />
              </div>

              <Campo
                label="Ponto de Referência"
                placeholder="Em frente à praça central"
                value={usuario.referencia}
                onChange={v => set('referencia', v)}
                dica="O locutor pode usar para orientar seus clientes"
                rightSlot={disclosureConsentToggle('referencia')}
              />

              <hr className="divider" />
              <div>
                <div className="group-label">Redes Sociais</div>

                <Campo
                  label="Instagram"
                  placeholder="@seucomercio"
                  value={usuario.instagram}
                  onChange={v => set('instagram', v)}
                  dica="O locutor pode divulgar suas redes nas locuções"
                  rightSlot={disclosureConsentToggle('instagram')}
                />

                <Campo
                  label="Facebook"
                  placeholder="facebook.com/seucomercio"
                  value={usuario.facebook}
                  onChange={v => set('facebook', v)}
                  dica="O locutor pode divulgar suas redes nas locuções"
                  rightSlot={disclosureConsentToggle('facebook')}
                />

                <Campo
                  label="TikTok"
                  placeholder="@seucomercio"
                  value={usuario.tiktok}
                  onChange={v => set('tiktok', v)}
                  dica="O locutor pode divulgar suas redes nas locuções"
                  rightSlot={disclosureConsentToggle('tiktok')}
                />

                <Campo
                  label="Site"
                  placeholder="www.seucomercio.com.br"
                  value={usuario.site}
                  onChange={v => set('site', v)}
                  dica="O locutor pode divulgar suas redes nas locuções"
                  rightSlot={disclosureConsentToggle('site')}
                />
              </div>
            </>
          )}

          {/* ═══════════════════════════════════════════ */}
          {/* STEP 4 — FUNCIONAMENTO */}
          {/* ═══════════════════════════════════════════ */}
          {step === 5 && (
            <>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
                O locutor usa estes horários para informar seus clientes.
              </div>

              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-400)', marginBottom: 8 }}>
                Segunda a Sexta
              </div>
              <Toggle
                label="Aberto 24 horas"
                value={usuario.aberto24h}
                onChange={v => {
                  set('aberto24h', v);
                  if (v) { set('horarioAbre', ''); set('horarioFecha', ''); }
                }}
                dica="O locutor não menciona horário — loja nunca fecha"
              />
              {!usuario.aberto24h && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16, marginTop: 8 }}>
                  <Campo
                    label="Abre"
                    placeholder="07:00"
                    value={usuario.horarioAbre}
                    onChange={v => set('horarioAbre', v)}
                    tipo="time"
                  />
                  <Campo
                    label="Fecha"
                    placeholder="20:00"
                    value={usuario.horarioFecha}
                    onChange={v => set('horarioFecha', v)}
                    tipo="time"
                  />
                </div>
              )}

              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-400)', marginBottom: 8, marginTop: 16 }}>
                Sábado
              </div>
              <Toggle
                label="Aberto 24 horas"
                value={usuario.aberto24hSabado}
                onChange={v => {
                  set('aberto24hSabado', v);
                  if (v) { set('horarioSabadoAbre', ''); set('horarioSabadoFecha', ''); }
                }}
                dica="O locutor não menciona horário — loja nunca fecha"
              />
              {!usuario.aberto24hSabado && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16, marginTop: 8 }}>
                  <Campo
                    label="Abre"
                    value={usuario.horarioSabadoAbre}
                    onChange={v => set('horarioSabadoAbre', v)}
                    tipo="time"
                  />
                  <Campo
                    label="Fecha"
                    value={usuario.horarioSabadoFecha}
                    onChange={v => set('horarioSabadoFecha', v)}
                    tipo="time"
                  />
                </div>
              )}

              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-400)', marginBottom: 8, marginTop: 16 }}>
                Domingo
              </div>
              <Toggle
                label="Aberto 24 horas"
                value={usuario.aberto24hDomingo}
                onChange={v => {
                  set('aberto24hDomingo', v);
                  if (v) { set('horarioDomingoAbre', ''); set('horarioDomingoFecha', ''); set('domingoFechado', false); }
                }}
                dica="O locutor não menciona horário — loja nunca fecha"
              />
              {!usuario.aberto24hDomingo && (
                <>
                  <Toggle
                    label="Fechado aos domingos"
                    value={usuario.domingoFechado}
                    onChange={v => set('domingoFechado', v)}
                  />
                  {!usuario.domingoFechado && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                      <Campo
                        label="Abre"
                        value={usuario.horarioDomingoAbre}
                        onChange={v => set('horarioDomingoAbre', v)}
                        tipo="time"
                      />
                      <Campo
                        label="Fecha"
                        value={usuario.horarioDomingoFecha}
                        onChange={v => set('horarioDomingoFecha', v)}
                        tipo="time"
                      />
                    </div>
                  )}
                </>
              )}

              <hr className="divider" />
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
                  Delivery, estacionamento e serviços são capacidades declaradas da loja. Eles não definem o perfil social da rádio e só aparecem quando houver fato e autorização próprios.
                </div>
                <Toggle
                  label="Faz delivery / entrega"
                  value={usuario.delivery}
                  onChange={v => set('delivery', v)}
                  dica="O locutor avisa os clientes que você entrega"
                  rightSlot={semanticConsentToggle('delivery')}
                />
                {usuario.tipoProduto === 'indoor' ? (
                  <div style={{
                    padding: '9px 0', color: 'var(--text-secondary)', fontSize: 12,
                    borderTop: '1px solid rgba(var(--neutral-300-rgb),0.3)',
                  }}>
                    Estacionamento é declarado na planta, pela área física de estacionamento.
                  </div>
                ) : (
                  <Toggle
                    label="Tem estacionamento"
                    value={usuario.estacionamento}
                    onChange={v => set('estacionamento', v)}
                    dica="O locutor menciona que tem estacionamento disponível"
                    rightSlot={semanticConsentToggle('estacionamento')}
                  />
                )}
                <Toggle
                  label="Iniciar rádio automaticamente no horário de funcionamento"
                  value={usuario.autoIniciarHorario || false}
                  onChange={v => set('autoIniciarHorario', v)}
                  dica="Se desativado, o play precisa ser dado manualmente pelo operador."
                />

              </div>
            </>
          )}

          {/* ═══════════════════════════════════════════ */}
          {/* STEP 5 — PERSONALIZAÇÃO DO LOCUTOR */}
          {/* ═══════════════════════════════════════════ */}
          {step === 6 && (
            <>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Aqui você ensina o locutor a falar como se fosse um funcionário da sua loja — com o seu jeito, o seu tom, as suas palavras. O que ficar vazio será ignorado.
              </div>

              <CampoTexto
                label="Como é o movimento habitual da loja?"
                placeholder="Ex: Mais movimentada cedo e no fim da tarde; tranquila após o almoço"
                value={usuario.ritmoAmbiente}
                onChange={value => set('ritmoAmbiente', value)}
                dica="Descreva o movimento da loja. O tom da rádio é informado abaixo."
                rightSlot={semanticConsentToggle('ritmoAmbiente')}
              />

              <hr className="divider" />

              <div>
                <div className="group-label">Cliente Ouvinte Da Loja</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  Responda como falaria numa conversa. Campos vazios serão ignorados.
                </div>
                <CampoTexto
                  label="Como é o cliente que mais compra aqui?"
                  placeholder="Ex: pessoal do bairro, famílias que compram para casa, gente que passa depois do trabalho"
                  value={usuario.clienteDescricaoLivre}
                  onChange={v => set('clienteDescricaoLivre', v)}
                  rightSlot={semanticConsentToggle('clienteDescricaoLivre')}
                />
                <details style={{ marginBottom: 12 }}>
                  <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12 }}>
                    Contexto avançado do público (opcional)
                  </summary>
                  <div style={{ marginTop: 10 }}>
                    <CampoTexto
                      label="Como esse cliente costuma comprar?"
                      placeholder="Ex: compra picada todo dia, compra do mês, passa rápido para completar a lista"
                      value={usuario.clienteRotinaCompra}
                      onChange={v => { setRoutineEdited(true); set('clienteRotinaCompra', v); }}
                      dica="Campo legado preservado; não é usado como fato sobre uma pessoa específica."
                      rightSlot={semanticConsentToggle('clienteRotinaCompra')}
                    />
                    <CadastralClarificationReview tenantId={usuario.id} sourceKind="PROFILE_FIELD"
                      sourceId="profile:clienteRotinaCompra" declaration={usuario.clienteRotinaCompra || ''}
                      sourceReady={!loading && !routineEdited} refreshToken={clarificationRefresh} />
                    <CampoTexto
                      label="O que ele mais valoriza na loja?"
                      placeholder="Ex: preço justo, atendimento, variedade, rapidez, produto fresco"
                      value={usuario.clienteValoriza}
                      onChange={v => set('clienteValoriza', v)}
                      dica="Campo legado preservado; modula apenas contexto social."
                      rightSlot={semanticConsentToggle('clienteValoriza')}
                    />
                  </div>
                </details>

                <CampoTexto
                  label="Como a rádio deve soar?"
                  placeholder="Ex: Próxima e simples, sem gritaria"
                  value={usuario.radioTomDesejado}
                  onChange={value => set('radioTomDesejado', value)}
                  rightSlot={semanticConsentToggle('radioTomDesejado')}
                />
                <CampoTexto
                  label="O que a rádio deve evitar?"
                  placeholder="Ex: pressão, linguagem chique, piada, insistência, falar promoção sem oferta"
                  value={usuario.radioEvitar}
                  onChange={v => set('radioEvitar', v)}
                  rightSlot={semanticConsentToggle('radioEvitar')}
                />
              </div>

              {/* ── Voz do Locutor ── */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>
                  Voz do Locutor
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6 }}>
                    — escolha quem vai falar na sua loja
                  </span>
                </label>
                <PersonaIndoorSelector
                  clienteId={usuario.id}
                  personaAtual={usuario.locutorIndoor}
                  onChange={(personaId) => set('locutorIndoor', personaId)}
                />
              </div>

              <hr className="divider" />

              <Campo
                label="Saudação personalizada"
                placeholder="Ex: Seja bem-vindo à"
                value={usuario.saudacaoPersonalizada}
                onChange={value => set('saudacaoPersonalizada', value)}
              />

              <Campo
                label="Como chamar o cliente"
                placeholder="Ex: cliente amigo, amigo ouvinte"
                value={usuario.cliente}
                onChange={v => set('cliente', v)}
                dica="Aparece em: 'Bom dia, cliente amigo!'"
                rightSlot={semanticConsentToggle('cliente')}
              />

              <hr className="divider" />
              <div>
                  <div className="group-label">Saudações por Período</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
                  Bom dia até 8h59 e boa noite a partir de 18h. A saudação operacional será usada uma vez na transição de cada período.
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <Campo
                    label="Manhã (até 9h)"
                    placeholder="Bom dia"
                    value={usuario.saudacaoManha}
                    onChange={v => set('saudacaoManha', v)}
                  />
                  <Campo
                    label="Tarde"
                    placeholder="Boa tarde"
                    value={usuario.saudacaoTarde}
                    onChange={v => set('saudacaoTarde', v)}
                  />
                  <Campo
                    label="Noite (a partir de 18h)"
                    placeholder="Boa noite"
                    value={usuario.saudacaoNoite}
                    onChange={v => set('saudacaoNoite', v)}
                  />
                </div>
              </div>

              <hr className="divider" />
              <div>
                <div className="group-label">Frases Especiais</div>

                <Campo
                  label="Frase de Abertura (manhã)"
                  placeholder="Ex: Abriu, é oferta! Bom dia!"
                  value={usuario.fraseAbertura}
                  onChange={v => set('fraseAbertura', v)}
                  dica="Usada na primeira locução do dia"
                />

                <Campo
                  label="Frase de Fechamento (noite)"
                  placeholder="Ex: Agradecemos sua visita, volte sempre!"
                  value={usuario.fraseFechamento}
                  onChange={v => set('fraseFechamento', v)}
                  dica="Usada na última locução do dia"
                />

                <Campo
                  label="Frase Promocional (rotativa)"
                  placeholder="Ex: Confira nossas ofertas do dia!"
                  value={usuario.frasePromocional}
                  onChange={v => set('frasePromocional', v)}
                  dica="Inserida aleatoriamente nas locuções"
                />
              </div>

              {/* PREVIEW */}
              <PreviewLocutor usuario={usuario} />
            </>
          )}

          {/* ═══════════════════════════════════════════ */}
          {/* MAPA DE SETORES — indoor only, após salvar */}
          {/* ═══════════════════════════════════════════ */}
          {/* ERRO DE SUBMIT */}
          {/* ═══════════════════════════════════════════ */}
          {saveNotice && (
            <div className="alert alert-info" style={{ marginTop: 16 }}>{saveNotice}</div>
          )}

          {errors.submit && (
            <div className="alert alert-error" style={{ marginTop: 16 }}>{errors.submit}</div>
          )}

          {/* ═══════════════════════════════════════════ */}
          {/* BOTÕES DE NAVEGAÇÃO */}
          {/* ═══════════════════════════════════════════ */}
          <div style={{
            display: 'flex', gap: 12, marginTop: 24,
            justifyContent: 'space-between'
          }}>
            <div>
              {step > 1 && (
                <button className="btn btn-secondary" onClick={voltar}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <ArrowIcon direction="left" size={11} /> Voltar
                  </span>
                </button>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              {step >= 4 && step < TOTAL_STEPS && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setStep(TOTAL_STEPS)}
                  style={{ fontSize: 12 }}
                >
                  Pular para Preview
                </button>
              )}

              <button
                className="btn"
                onClick={avancar}
                disabled={loading}
              >
                {loading
                  ? 'Salvando...'
                  : step < TOTAL_STEPS
                    ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        Próximo <ArrowIcon direction="right" size={11} />
                      </span>
                    )
                    : isEdicao ? 'Salvar Configurações' : publicInviteMode ? 'Criar acesso' : 'Finalizar Cadastro'
                }
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CadastroScreen;
